import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import GenericFileUpload from '../../../../../../../components/common/GenericFileUpload';
import WarrantyDocumentTypeDropdown from '../../../../../../../components/common/WarrantyDocumentTypeDropdown';
import ItemCodeDropdownwithId from '../../../../../../../components/common/ItemCodeDropdownwithId';
import { useUploadWarrantyClaimDocumentMutation, useUpdateWarrantyClaimDocumentMutation, useLazyGetWarrantyClaimDocumentByIdQuery, useDeleteWarrantyClaimDocumentMutation } from '../../../../../../../api/endpoints/warrantyClaims';
import { useLazyDownloadDocumentByKeyQuery } from '../../../../../../../api/endpoints/documentContent';
import DocumentDownloadService from '../../../../../../../utils/documentDownloadService';
import toastService from '../../../../../../../utils/toastService';
import { useLanguage } from '../../../../../../../contexts/LanguageContext';

const AttachedDocumentsFormDialog = ({ disabledStatuses, claimId, dialogVisible, hideDialog, onDelete, editingDocument, onUpdateSuccess }) => {
    const { th } = useLanguage();
    const [formData, setFormData] = useState({
        id: null,
        claimId: claimId,
        description: '',
        file: null,
        subModuleType: '',
        subReferenceId: null
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [updatedFiles, setUpdatedFiles] = useState([]);
    const [showUploadingModal, setShowUploadingModal] = useState(false);

    // Create toast ref and document download service
    const toastRef = useRef(null);
    const [documentDownloadService] = useState(() => new DocumentDownloadService(toastRef));

    // Document management mutations and queries
    const [uploadDocument] = useUploadWarrantyClaimDocumentMutation();
    const [updateDocument] = useUpdateWarrantyClaimDocumentMutation();
    const [deleteDocument] = useDeleteWarrantyClaimDocumentMutation();
    const [triggerDownloadByKey, { isLoading: isDownloadingDocument }] = useLazyDownloadDocumentByKeyQuery();

    // Get document details when editing - her dialog açılışında API isteği at
    const [getDocumentById, { data: documentDetails, isLoading: isLoadingDocumentDetails }] = useLazyGetWarrantyClaimDocumentByIdQuery();

    const resetForm = () => {
        setFormData({
            id: null,
            claimId: claimId,
            description: '',
            file: null,
            subModuleType: '',
            subReferenceId: null
        });
        setUpdatedFiles([]);
        setErrors({});
        setIsSubmitting(false);
    };

    const handleDialogClose = () => {
        resetForm();
        hideDialog();
    };

    useEffect(() => {
        if (dialogVisible) {
            if (editingDocument) {
                // Sadece temel bilgileri set et, diğer veriler API'den gelecek
                setFormData({
                    id: editingDocument.id,
                    claimId: claimId,
                    description: '',
                    file: null,
                    subModuleType: '',
                    subReferenceId: null
                });

                // Dialog açıldığında API isteği at
                getDocumentById(editingDocument.id);
            } else {
                resetForm();
            }
        }
    }, [dialogVisible, editingDocument, claimId, getDocumentById]);

    // Update form data when document details are loaded from API
    useEffect(() => {
        if (documentDetails && documentDetails.data && editingDocument) {
            const docData = documentDetails.data;

            // Tüm verileri API'den gelen veri ile doldur
            setFormData((prev) => ({
                ...prev,
                description: docData.description || '',
                subModuleType: docData.subModuleType !== null && docData.subModuleType !== undefined ? docData.subModuleType : '',
                subReferenceId: docData.subReferenceId !== null && docData.subReferenceId !== undefined ? docData.subReferenceId : null
            }));
        }
    }, [documentDetails, editingDocument, dialogVisible]);

    useEffect(() => {
        if (!dialogVisible) {
            resetForm();
        }
    }, [dialogVisible]);

    // Clear subReferenceId when subModuleType changes and it's not "Parça Fotoğrafı" (code: 1)
    useEffect(() => {
        if (formData.subModuleType !== 1) {
            setFormData((prev) => ({ ...prev, subReferenceId: null }));
        }
    }, [formData.subModuleType]);

    const onEditFilesChange = (files) => {
        setUpdatedFiles(files);
        setErrors((prev) => ({ ...prev, file: null }));
    };

    const handleDeleteDocument = async (documentData, event) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        if (window.confirm(`'${documentData.fileName}' dosyasını silmek istediğinizden emin misiniz?`)) {
            try {
                await deleteDocument({
                    id: claimId,
                    documentId: documentData.id
                }).unwrap();
                toastService.success('Dosya başarıyla silindi.');
                hideDialog();
            } catch (error) {
                console.error('Error deleting document:', error);
                const errorMessage = error?.data?.message || error?.message || 'Bilinmeyen bir hata oluştu.';
                toastService.error(`Dosya silinemedi. Detay: ${errorMessage}`);
            }
        }
    };

    const handleUpdateDocument = async (documentData, event) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        if (!claimId || !documentData.newFile) {
            toastService.error('Güncelleme için dosya seçilmedi.');
            return;
        }

        try {
            await updateDocument({
                DocumentId: documentData.id,
                WarrantyClaimId: claimId,
                Description: formData.description || documentData.newFile.name,
                SubModuleType: formData.subModuleType || 0,
                SubReferenceId: formData.subReferenceId,
                File: documentData.newFile
            }).unwrap();

            toastService.success('Dosya başarıyla güncellendi.');
            hideDialog();
        } catch (error) {
            console.error('Error updating document:', error);
            const errorMessage = error?.data?.message || error?.message || 'Bilinmeyen bir hata oluştu.';
            toastService.error(`Dosya güncellenemedi. Detay: ${errorMessage}`);
        }
    };

    const handleDownloadDocument = async (documentData, event) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        try {
            if (documentDownloadService.toastRef !== toastRef) {
                documentDownloadService.toastRef = toastRef;
            }

            await documentDownloadService.downloadByKey(triggerDownloadByKey, documentData.fileKey, documentData, {
                successMessage: 'Dosya başarıyla indirildi.',
                errorMessage: 'Dosya indirilemedi.'
            });
        } catch (error) {
            console.error('Download failed:', error);
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!editingDocument && updatedFiles.length === 0) {
            newErrors.file = th('warrantyClaims.detail.documents.fileRequired');
        }

        // If subModuleType is 1 (Parça Fotoğrafı), subReferenceId is required
        if (formData.subModuleType == 1 && !formData.subReferenceId) {
            newErrors.subReferenceId = th('warrantyClaims.detail.documents.partCodeRequired');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        try {
            if (updatedFiles.length > 0) {
                if (editingDocument) {
                    // Update existing document with new file
                    const result = await updateDocument({
                        DocumentId: editingDocument.id,
                        WarrantyClaimId: claimId,
                        Description: formData.description || updatedFiles[0].name,
                        SubModuleType: formData.subModuleType || 0,
                        SubReferenceId: formData.subReferenceId,
                        File: updatedFiles[0]
                    }).unwrap();

                    if (result) {
                        toastService.success('Dosya başarıyla güncellendi.');
                        if (onUpdateSuccess) {
                            onUpdateSuccess();
                        }
                        resetForm();
                        hideDialog();
                    }
                } else {
                    // Upload multiple files - her dosya için ayrı API çağrısı yap
                    let successCount = 0;
                    let errorCount = 0;

                    // Yükleme modal'ını aç
                    setShowUploadingModal(true);

                    try {
                        for (const file of updatedFiles) {
                            try {
                                const result = await uploadDocument({
                                    claimId: claimId,
                                    file: file,
                                    description: formData.description || file.name,
                                    subModuleType: formData.subModuleType,
                                    subReferenceId: formData.subReferenceId
                                }).unwrap();

                                if (result) {
                                    successCount++;
                                }
                            } catch (error) {
                                console.error(`Dosya yükleme hatası (${file.name}):`, error);
                                errorCount++;
                            }
                        }

                        if (successCount > 0) {
                            if (updatedFiles.length === 1) {
                                toastService.success('Dosya başarıyla yüklendi.');
                            } else {
                                toastService.success(`${successCount} dosya başarıyla yüklendi.${errorCount > 0 ? ` ${errorCount} dosya yüklenemedi.` : ''}`);
                            }
                            resetForm();
                            hideDialog();
                        } else {
                            toastService.error('Dosyalar yüklenemedi. Lütfen tekrar deneyin.');
                        }
                    } finally {
                        // API işlemi bittiğinde modal'ı kapat
                        setShowUploadingModal(false);
                    }
                }
            } else if (editingDocument) {
                // Check if any field has changed from the original document
                const originalDescription = documentDetails?.data?.description || '';
                const originalSubModuleType = documentDetails?.data?.subModuleType || 0;
                const originalSubReferenceId = documentDetails?.data?.subReferenceId || null;

                const hasChanges = formData.description !== originalDescription || formData.subModuleType !== originalSubModuleType || formData.subReferenceId !== originalSubReferenceId;

                if (hasChanges) {
                    // Update document info only
                    try {
                        await updateDocument({
                            DocumentId: editingDocument.id,
                            WarrantyClaimId: claimId,
                            Description: formData.description || '',
                            SubModuleType: formData.subModuleType || 0,
                            SubReferenceId: formData.subReferenceId,
                            File: null // No file update
                        }).unwrap();

                        toastService.success('Doküman bilgileri başarıyla güncellendi.');
                        if (onUpdateSuccess) {
                            onUpdateSuccess();
                        }
                        resetForm();
                        hideDialog();
                    } catch (error) {
                        console.error('Error updating document info:', error);
                        const errorMessage = error?.data?.message || error?.message || 'Bilinmeyen bir hata oluştu.';
                        toastService.error(`Güncelleme hatası: ${errorMessage}`);
                    }
                } else {
                    // No changes made
                    toastService.info(th('warrantyClaims.detail.documents.noChangesMade'));
                    hideDialog();
                }
            }
        } catch (error) {
            console.error('Kaydetme hatası:', error);
            const errorMessage = error?.data?.message || error?.message || 'Bilinmeyen bir hata oluştu.';
            toastService.error(`Kaydetme hatası: ${errorMessage}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async () => {
        if (!editingDocument?.id) return;

        setIsSubmitting(true);
        try {
            const success = await onDelete(editingDocument.id);
            if (success) {
                toastService.success('Dosya başarıyla silindi.');
                resetForm();
                hideDialog();
            }
        } catch (error) {
            console.error('Silme hatası:', error);
            const errorMessage = error?.data?.message || error?.message || 'Bilinmeyen bir hata oluştu.';
            toastService.error(`Silme hatası: ${errorMessage}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    const footer = (
        <div className="flex justify-content-between items-center">
            {editingDocument && <Button label={th('warrantyClaims.detail.documents.delete')} icon="pi pi-trash" className="p-button-danger" onClick={handleDelete} disabled={disabledStatuses} />}
            <div className="flex gap-2 justify-content-end w-full">
                <Button label={th('warrantyClaims.detail.documents.cancel')} icon="pi pi-times" onClick={handleDialogClose} outlined disabled={isSubmitting} />
                <Button label={editingDocument ? th('warrantyClaims.detail.documents.update') : th('warrantyClaims.detail.documents.create')} icon="pi pi-check" onClick={handleSave} disabled={isSubmitting || disabledStatuses} />
            </div>
        </div>
    );

    return (
        <>
            <Dialog
                visible={dialogVisible}
                style={{ width: '50rem' }}
                header={editingDocument ? th('warrantyClaims.detail.documents.editTitle') : th('warrantyClaims.detail.documents.addTitle')}
                modal
                className="p-fluid"
                footer={footer}
                onHide={handleDialogClose}
            >
                <div className="field mb-2">
                    <label htmlFor="description">{th('warrantyClaims.detail.documents.description')}</label>
                    <InputText
                        id="description"
                        value={formData.description}
                        onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                        placeholder={th('warrantyClaims.detail.documents.descriptionPlaceholder')}
                        disabled={isSubmitting || disabledStatuses || editingDocument}
                    />
                </div>
                <div className="field mb-2">
                    <label htmlFor="subModuleType">{th('warrantyClaims.detail.documents.documentType')}</label>
                    <WarrantyDocumentTypeDropdown
                        value={formData.subModuleType}
                        onChange={(value) => setFormData((prev) => ({ ...prev, subModuleType: value }))}
                        className="w-full"
                        placeholder={th('warrantyClaims.detail.documents.documentTypePlaceholder')}
                        disabled={isSubmitting || disabledStatuses || editingDocument}
                    />
                </div>
                {formData.subModuleType == 1 && (
                    <div className="field mb-2">
                        <label htmlFor="subReferenceId">{th('warrantyClaims.detail.documents.partCode')} *</label>
                        <ItemCodeDropdownwithId
                            warrantyClaimId={claimId}
                            value={formData.subReferenceId}
                            onChange={(value) => setFormData((prev) => ({ ...prev, subReferenceId: value?.id || null }))}
                            className="w-full"
                            disabled={isSubmitting || disabledStatuses || editingDocument}
                        />
                        {errors.subReferenceId && <small className="p-error">{errors.subReferenceId}</small>}
                    </div>
                )}
                <div className="field mb-2">
                    <label htmlFor="file">{th('warrantyClaims.detail.documents.file')} *</label>
                    <GenericFileUpload
                        newFiles={!disabledStatuses ? updatedFiles || [] : []}
                        onFilesChange={!disabledStatuses ? onEditFilesChange : () => {}}
                        existingDocuments={editingDocument ? [editingDocument] : []}
                        onDownloadDocument={handleDownloadDocument}
                        onDeleteExistingDocument={!disabledStatuses ? handleDeleteDocument : null}
                        onUpdateExistingDocument={!disabledStatuses ? handleUpdateDocument : null}
                        isLoading={isSubmitting}
                        disabled={disabledStatuses}
                        multiple={true}
                        maxFileSize={20971520} // 20MB
                        acceptedFileTypes={{
                            'image/*': ['.png', '.jpg', '.jpeg', '.gif'],
                            'application/pdf': ['.pdf'],
                            'application/msword': ['.doc'],
                            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
                            'application/vnd.ms-excel': ['.xls'],
                            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
                            'text/plain': ['.txt'],
                            'application/x-rar-compressed': ['.rar'],
                            'application/vnd.rar': ['.rar'],
                            'application/zip': ['.zip'],
                            'message/rfc822': ['.eml'],
                            'application/vnd.ms-outlook': ['.msg']
                        }}
                        showDownloadButton={true}
                        showUpdateButton={!disabledStatuses}
                        showDeleteButton={!disabledStatuses}
                        readOnlyMode={disabledStatuses}
                    />
                    {errors.file && <small className="p-error">{errors.file}</small>}
                </div>
            </Dialog>

            {/* Yükleme İşlemi Modal'ı */}
            <Dialog visible={showUploadingModal} style={{ width: '30rem' }} header={th('warrantyClaims.detail.documents.uploadingTitle')} modal closable={false} draggable={false}>
                <div className="flex flex-column align-items-center justify-content-center py-4">
                    <i className="pi pi-spin pi-spinner" style={{ fontSize: '3rem', color: 'var(--primary-color)' }}></i>
                    <p className="mt-3 mb-0 text-xl font-semibold">{th('warrantyClaims.detail.documents.operationInProgress')}</p>
                    <p className="mt-2 mb-0 text-sm text-color-secondary">{th('warrantyClaims.detail.documents.pleaseWait')}</p>
                </div>
            </Dialog>
        </>
    );
};

export default AttachedDocumentsFormDialog;
